/**
 * job-status — canonical job-status presentation helpers (Quick 260614-s5w).
 *
 * SINGLE source of truth for status-code → color variant, display label, and
 * group ordering across the dispatcher web app. Previously StatusBadge and
 * JobQueue hard-coded NON-EXISTENT codes (EN_ROUTE_PICKUP, ONSITE_PICKUP,
 * DISPOSAL_COMPLETE, …) that never match the API. The canonical 16 codes are
 * defined in @lodex-pro/shared enums.ts (master spec §8) — this module mirrors
 * them for the web UI:
 *   PENDING, EN_ROUTE_TO_PICKUP, ARRIVED_PICKUP, ONSITE_WORK_START,
 *   ONSITE_WORK_END, EN_ROUTE_TO_DROP_OFF, DISPOSAL_IN, UNLOADING_START,
 *   DISPOSAL_OUT, COMPLETED, IN_REVIEW, TO_BE_INVOICED, INVOICED, PAUSED,
 *   CANCELLED, REJECTED.
 *
 * The mismatch caused: grey/neutral badges instead of the right color (the
 * driver-stack "wrong status" report), wrong queue grouping, and a status
 * filter dropdown offering codes the API rejects. ABBR labels keep the dense
 * job-queue STATUS column from truncating; the full label is shown on hover.
 */

export type StatusVariant =
  | 'warning'
  | 'accent'
  | 'success'
  | 'danger'
  | 'neutral'
  | 'muted'
  | 'orange'
  | 'teal'
  | 'yellow';

/**
 * Color variant per status code. Quick 260615-qgd (#2) — expanded the palette
 * so the lifecycle reads at a glance per the requested spec: PENDING=amber,
 * EN_ROUTE_TO_PICKUP=blue, ARRIVED_PICKUP=orange, EN_ROUTE_TO_DROP_OFF=teal,
 * COMPLETED=green, PAUSED=yellow, REJECTED=red. (Wave 1 #6 removed the status
 * COLUMN, which is why the colors disappeared from the queue — they now color
 * the collapsible group headers instead.)
 */
export function getStatusVariant(statusCode: string): StatusVariant {
  switch (statusCode) {
    case 'PENDING':
      return 'warning'; // amber
    case 'EN_ROUTE_TO_PICKUP':
    case 'ONSITE_WORK_START':
    case 'ONSITE_WORK_END':
      return 'accent'; // blue
    case 'ARRIVED_PICKUP':
      return 'orange';
    case 'EN_ROUTE_TO_DROP_OFF':
      return 'teal';
    case 'DISPOSAL_IN':
    case 'UNLOADING_START':
    case 'DISPOSAL_OUT':
      return 'warning'; // amber (unspecified — kept in the in-flight family)
    case 'PAUSED':
      return 'yellow';
    case 'COMPLETED':
      return 'success'; // green
    case 'IN_REVIEW':
    case 'REJECTED':
      return 'danger'; // red
    case 'TO_BE_INVOICED':
    case 'INVOICED':
      return 'neutral';
    case 'CANCELLED':
      return 'muted';
    default:
      return 'neutral';
  }
}

/** Foreground / background / border for a variant — single source for badges + headers. */
export interface VariantColor {
  readonly fg: string;
  readonly bg: string;
  readonly border: string;
}

export const STATUS_VARIANT_COLORS: Record<StatusVariant, VariantColor> = {
  warning: {
    fg: 'var(--color-warning)',
    bg: 'rgba(246, 182, 94, 0.15)',
    border: 'rgba(246, 182, 94, 0.3)',
  },
  accent: {
    fg: 'var(--color-accent-blue)',
    bg: 'rgba(123, 150, 232, 0.15)',
    border: 'rgba(123, 150, 232, 0.3)',
  },
  success: {
    fg: 'var(--color-success)',
    bg: 'rgba(111, 207, 142, 0.15)',
    border: 'rgba(111, 207, 142, 0.3)',
  },
  danger: {
    fg: 'var(--color-danger)',
    bg: 'rgba(243, 139, 139, 0.15)',
    border: 'rgba(243, 139, 139, 0.3)',
  },
  neutral: {
    fg: 'var(--color-text-secondary)',
    bg: 'rgba(125, 130, 142, 0.1)',
    border: 'rgba(125, 130, 142, 0.2)',
  },
  muted: {
    fg: 'var(--color-text-tertiary)',
    bg: 'rgba(125, 130, 142, 0.1)',
    border: 'rgba(125, 130, 142, 0.15)',
  },
  orange: {
    fg: 'var(--color-tone-orange)',
    bg: 'rgba(242, 144, 92, 0.15)',
    border: 'rgba(242, 144, 92, 0.35)',
  },
  teal: {
    fg: 'var(--color-tone-teal)',
    bg: 'rgba(95, 196, 192, 0.15)',
    border: 'rgba(95, 196, 192, 0.35)',
  },
  yellow: {
    fg: 'var(--color-tone-yellow)',
    bg: 'rgba(232, 210, 122, 0.15)',
    border: 'rgba(232, 210, 122, 0.35)',
  },
};

/** Resolved colors for a status code (variant → colors). */
export function statusColor(statusCode: string): VariantColor {
  return STATUS_VARIANT_COLORS[getStatusVariant(statusCode)];
}

/** Full human-readable label (shown as the badge tooltip + group headers). */
const STATUS_FULL: Record<string, string> = {
  PENDING: 'Pending',
  EN_ROUTE_TO_PICKUP: 'En route to pickup',
  ARRIVED_PICKUP: 'Arrived at pickup',
  ONSITE_WORK_START: 'Onsite work started',
  ONSITE_WORK_END: 'Onsite work ended',
  EN_ROUTE_TO_DROP_OFF: 'En route to drop-off',
  DISPOSAL_IN: 'Disposal — checked in',
  UNLOADING_START: 'Unloading started',
  DISPOSAL_OUT: 'Disposal — checked out',
  COMPLETED: 'Completed',
  IN_REVIEW: 'In review',
  TO_BE_INVOICED: 'To be invoiced',
  INVOICED: 'Invoiced',
  PAUSED: 'Paused',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
};

/** Short label (≤12 chars) for the dense job-queue STATUS column + badges. */
const STATUS_ABBR: Record<string, string> = {
  PENDING: 'PENDING',
  EN_ROUTE_TO_PICKUP: 'EN RT PICKUP',
  ARRIVED_PICKUP: 'AT PICKUP',
  ONSITE_WORK_START: 'WORK START',
  ONSITE_WORK_END: 'WORK END',
  EN_ROUTE_TO_DROP_OFF: 'EN RT DROP',
  DISPOSAL_IN: 'DISP IN',
  UNLOADING_START: 'UNLOADING',
  DISPOSAL_OUT: 'DISP OUT',
  COMPLETED: 'COMPLETED',
  IN_REVIEW: 'REVIEW',
  TO_BE_INVOICED: 'TO INVOICE',
  INVOICED: 'INVOICED',
  PAUSED: 'PAUSED',
  CANCELLED: 'CANCELLED',
  REJECTED: 'REJECTED',
};

/** Full label for a code; falls back to a humanized form for unknown codes. */
export function statusFullLabel(statusCode: string): string {
  return STATUS_FULL[statusCode] ?? statusCode.replace(/_/g, ' ');
}

/** Abbreviated label for a code; falls back to the humanized form. */
export function statusAbbrLabel(statusCode: string): string {
  return STATUS_ABBR[statusCode] ?? statusCode.replace(/_/g, ' ');
}

/**
 * Canonical lifecycle order for grouping + the status filter dropdown.
 * Mirrors enums.ts JOB_STATUS_CODES but ordered for dispatcher workflow
 * (active chain first, then PAUSED/review/rejected, then billing, then closed).
 */
export const STATUS_ORDER: readonly string[] = [
  'PENDING',
  'EN_ROUTE_TO_PICKUP',
  'ARRIVED_PICKUP',
  'ONSITE_WORK_START',
  'ONSITE_WORK_END',
  'EN_ROUTE_TO_DROP_OFF',
  'DISPOSAL_IN',
  'UNLOADING_START',
  'DISPOSAL_OUT',
  'PAUSED',
  'IN_REVIEW',
  'REJECTED',
  'TO_BE_INVOICED',
  'COMPLETED',
  'INVOICED',
  'CANCELLED',
];

/**
 * Statuses offered in the dispatcher's STATUS filter dropdown (Quick 260614-ut1 / #1).
 * Subset of STATUS_ORDER — excludes billing/admin states the dispatcher does not
 * triage by (IN_REVIEW, TO_BE_INVOICED, INVOICED, CANCELLED). The full STATUS_ORDER
 * still drives grouping + the badge catalog.
 */
export const DISPATCHER_FILTER_STATUSES: readonly string[] = [
  'PENDING',
  'EN_ROUTE_TO_PICKUP',
  'ARRIVED_PICKUP',
  'ONSITE_WORK_START',
  'ONSITE_WORK_END',
  'EN_ROUTE_TO_DROP_OFF',
  'DISPOSAL_IN',
  'UNLOADING_START',
  'DISPOSAL_OUT',
  'PAUSED',
  'REJECTED',
  'COMPLETED',
];
